import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SubscriptionData {
  subscribed: boolean;
  subscription_tier?: string;
  subscription_end?: string;
}

interface SubscriptionContextType {
  subscription: SubscriptionData;
  isLoading: boolean;
  checkSubscription: () => Promise<void>;
  createCheckout: (plan: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  clearCache: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionData>({ subscribed: false });
  const [isLoading, setIsLoading] = useState(true);
  const [cacheTimestamp, setCacheTimestamp] = useState<number>(0);

  // Cache subscription data for 10 minutes to avoid repeated calls
  const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

  const checkSubscription = useCallback(async () => {
    // Use cache if data is fresh and we have subscription data
    const now = Date.now();
    if (now - cacheTimestamp < CACHE_DURATION && subscription.subscribed !== undefined) {
      console.log('🔄 Using cached subscription data');
      return;
    }

    try {
      // Don't check subscription if no user is logged in
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        console.log('⏭️ No authenticated user, skipping subscription check');
        setSubscription({ subscribed: false });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      console.log('📞 Checking subscription status...');
      
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('❌ Subscription check error:', error);
        throw error;
      }
      
      console.log('✅ Subscription check successful:', data);
      setSubscription(data);
      setCacheTimestamp(now);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setIsLoading(false);
    }
  }, [cacheTimestamp, isLoading]);

  const createCheckout = async (plan: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { plan }
      });
      
      if (error) throw error;
      
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Erreur lors de la création du checkout');
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      
      if (error) throw error;
      
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Erreur lors de l\'ouverture du portail client');
    }
  };

  const clearCache = useCallback(() => {
    setCacheTimestamp(0);
  }, []);

  // Check subscription on mount and when user session changes
  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // Clear cache and check subscription when user signs in
          clearCache();
          // Add delay to prevent multiple rapid calls
          setTimeout(() => checkSubscription(), 500);
        } else if (event === 'SIGNED_OUT') {
          // Reset subscription state on logout
          setSubscription({ subscribed: false });
          setIsLoading(false);
          clearCache();
        }
      }
    );

    // Initial check with delay to prevent conflicts
    const timer = setTimeout(() => checkSubscription(), 200);

    return () => {
      authSubscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  return (
    <SubscriptionContext.Provider value={{
      subscription,
      isLoading,
      checkSubscription,
      createCheckout,
      openCustomerPortal,
      clearCache
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};